import {
  useState,
  useEffect,
  useMemo,
  useContext,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Collapse,
  Autocomplete,
  Switch,
  FormControlLabel,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Event as EventIcon,
  TaskAlt as TaskAltIcon,
  Today as TodayIcon,
  Close as CloseIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DataContext } from "../contexts/DataProvider";
import { useAlert } from "../functions/useAlert";
import {
  fetchContactById,
  fetchAllContacts,
  deleteContact,
} from "../services/contacts.service";
import { useClientExpand } from "../hooks/useClientExpand";
import { useHomepageModals } from "../hooks/useHomepageModals";
import { fetchClientById, deleteClient } from "../services/clients.service";
import { fetchMeetingById, deleteMeeting } from "../services/meetings.service";
import { linkContact, unlinkContact } from "../services/relationships.service";
import {
  updateContactGraph,
  deleteContactGraph,
  deleteClientGraph,
} from "../functions/knowledgeGraphApi";
import Container from "../base/Container";

import Joyride from "react-joyride";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MeetingScheduler from "../components/MeetingScheduler";
import ContactModal from "./modals/ContactModal";
import ClientModal from "./modals/ClientModal";
import ImportContactDialog from "./common/ImportContactDialog";
import { useZohoCallback } from "../hooks/useZohoCallback";
import { useSchedulerState } from "../hooks/useSchedulerState";
import { useCRMSelection } from "../hooks/useCRMSelection";
import { useHomepageTour } from "../hooks/useHomepageTour";
import { useAIModalBridge } from "../hooks/useAIModalBridge";
import MeetingsPanel, { ListGranularity } from "./MeetingsPanel";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { getContactConfig } from "../utils/contactConfig";
import { homepageTourSteps } from "../tours";
import { useHomepageData } from "../hooks/useHomepageData";
import { useSessionState } from "../hooks/useSessionState";

dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const getMeetingStart = (meeting: any) =>
  meeting.utcStartTime
    ? dayjs(meeting.utcStartTime)
    : dayjs.tz(`${meeting.start?.date}T${meeting.start?.time}`, "Asia/Kolkata");

// Chart granularity for the list view. This replaced the old time-range /
// status / view filter bar (#2080) — the granularity *is* the range, so a
// separate range dropdown had nothing left to say. The type lives in
// MeetingsPanel to keep the import one-directional.
const LIST_GRANULARITIES: { value: ListGranularity; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
  { value: "fy", label: "FY" },
];

/**
 * The Australian financial year containing `d` — 1 Jul to 30 Jun.
 * July (month index 6) onwards belongs to the FY starting that calendar year.
 */
const financialYearStart = (d: dayjs.Dayjs): dayjs.Dayjs =>
  dayjs(`${d.month() >= 6 ? d.year() : d.year() - 1}-07-01`).startOf("day");

interface BucketDef {
  key: string;
  label: string;
  subLabel?: string;
  tooltipLabel?: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

/**
 * Buckets for the list-view chart at the chosen granularity, anchored on
 * `anchor` (the period the user has navigated to). Each granularity subdivides
 * into the next unit down, so a bar is always a meaningful click target:
 * day → hours, week → days, month → days, year → months.
 */
const buildBuckets = (
  granularity: ListGranularity,
  anchor: dayjs.Dayjs,
): { rangeStart: dayjs.Dayjs; rangeEnd: dayjs.Dayjs; buckets: BucketDef[] } => {
  if (granularity === "day") {
    const rangeStart = anchor.startOf("day");
    const rangeEnd = anchor.endOf("day");
    // 2-hour bars — 24 hourly bars is unreadable at this chart's width.
    const buckets: BucketDef[] = Array.from({ length: 12 }, (_, i) => {
      const s = rangeStart.add(i * 2, "hour");
      const e = s.add(2, "hour").subtract(1, "millisecond");
      return {
        key: s.format("YYYY-MM-DDTHH"),
        label: s.format("hA"),
        tooltipLabel: `${s.format("h:mm A")}–${e.add(1, "millisecond").format("h:mm A")}`,
        start: s,
        end: e,
      };
    });
    return { rangeStart, rangeEnd, buckets };
  }

  if (granularity === "week") {
    const rangeStart = anchor.startOf("isoWeek");
    const rangeEnd = anchor.endOf("isoWeek");
    const buckets: BucketDef[] = Array.from({ length: 7 }, (_, i) => {
      const d = rangeStart.add(i, "day");
      return {
        key: d.format("YYYY-MM-DD"),
        label: d.format("ddd"),
        subLabel: d.format("D MMM"),
        tooltipLabel: d.format("ddd D MMM YYYY"),
        start: d.startOf("day"),
        end: d.endOf("day"),
      };
    });
    return { rangeStart, rangeEnd, buckets };
  }

  if (granularity === "month") {
    const rangeStart = anchor.startOf("month");
    const rangeEnd = anchor.endOf("month");
    // One bar per ISO week of the month, clipped to the month's own edges so
    // counts can't leak in from the neighbouring months.
    const buckets: BucketDef[] = [];
    let cur = rangeStart.startOf("isoWeek");
    while (cur.isBefore(rangeEnd)) {
      const weekEnd = cur.endOf("isoWeek");
      const s = cur.isBefore(rangeStart) ? rangeStart : cur;
      const e = weekEnd.isAfter(rangeEnd) ? rangeEnd : weekEnd;
      buckets.push({
        key: s.format("YYYY-MM-DD"),
        label: `W${s.isoWeek()}`,
        subLabel: `${s.format("D MMM")}–${e.format("D MMM")}`,
        tooltipLabel: `${s.format("D MMM")}–${e.format("D MMM YYYY")}`,
        start: s,
        end: e,
      });
      cur = cur.add(1, "week").startOf("isoWeek");
    }
    return { rangeStart, rangeEnd, buckets };
  }

  // year / fy — 12 month bars, differing only in where the run starts.
  const rangeStart =
    granularity === "fy" ? financialYearStart(anchor) : anchor.startOf("year");
  const rangeEnd = rangeStart.add(11, "month").endOf("month");
  const buckets: BucketDef[] = Array.from({ length: 12 }, (_, i) => {
    const m = rangeStart.add(i, "month");
    return {
      key: m.format("YYYY-MM"),
      label: m.format("MMM"),
      // An FY straddles two calendar years, so mark the January where it turns.
      subLabel: i === 0 || m.month() === 0 ? m.format("YYYY") : undefined,
      tooltipLabel: m.format("MMMM YYYY"),
      start: m.startOf("month"),
      end: m.endOf("month"),
    };
  });
  return { rangeStart, rangeEnd, buckets };
};

interface Client {
  _id: string;
  name: string;
  entityType: string;
  orgId: string;
}
interface Advisor {
  _id: string;
  firstName: string;
  lastName: string;
}

interface Meeting {
  _id: string;
  clients: { _id: string; name: string }[];
  advisors: Advisor[];
  status: "Completed" | "Upcoming" | "Today" | "This Week";
  start: { date: string; time: string };
  end: { date: string; time: string };
  utcStartTime?: string;
  utcEndTime?: string;
  meetingType: string;
  meetingMedium: string;
  meetingLink?: string;
}

const KpiCard = ({
  title,
  value,
  change,
  icon,
  changeText = "from last month",
  subtitle = null
}: any) => (
  <Paper
    elevation={2}
    sx={{
      p: 2.5,
      borderRadius: "12px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
        <Typography variant="body1" fontWeight={600} color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Chip
            label={subtitle}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
        )}
      </Box>
      <Box sx={{ color: "primary.main" }}>{icon}</Box>
    </Box>
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
        {value}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        {change !== null && (
          <>
            {change >= 0 ? (
              <TrendingUpIcon sx={{ color: "success.main", fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ color: "error.main", fontSize: 18 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: change >= 0 ? "success.main" : "error.main",
                fontWeight: "medium",
              }}
            >
              {Math.abs(change).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {changeText}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  </Paper>
);
const LinkContactModal = ({
  open,
  onClose,
  onSave,
  client,
  orgId,
  userId,
  existingLinkedContacts = [],
  onCreateNew, // New prop to trigger the switch
}) => {
  // Removed Tabs and Step state. Simplification.
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [relationshipForm, setRelationshipForm] = useState({
    relationshipType: "",
    isPrimary: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [setAlert] = useAlert();

  const relationshipOptions = getContactConfig(client?.entityType).relationships;

  useEffect(() => {
    if (open) {
      setSelectedContact(null);
      setRelationshipForm({ relationshipType: "", isPrimary: false });

      const loadContacts = async () => {
        try {
          const response: any = await fetchAllContacts();
          setAllContacts(response.contacts || []);
        } catch {
          // non-critical — contact list stays empty
        }
      };
      loadContacts();
    }
  }, [open]);

  const availableContacts = useMemo(() => {
    if (!allContacts.length) return [];
    const linkedContactIds = new Set(existingLinkedContacts.map((c) => c._id));
    return allContacts.filter((contact) => !linkedContactIds.has(contact._id));
  }, [allContacts, existingLinkedContacts]);

  const handleLinkContact = async () => {
    setIsLoading(true);
    try {
      const payload = {
        clientId: client._id,
        contactId: selectedContact._id,
        relationshipType: relationshipForm.relationshipType,
        isPrimary: relationshipForm.isPrimary,
        orgId,
        userId,
      };

      const response: any = await linkContact(payload);

      if (response.error) throw new Error(response.error);

      setAlert("Contact linked successfully!", "success");
      updateContactGraph({
        ...payload,
        _id: payload.contactId,
      }).catch(() => {});
      try {
        window.dispatchEvent(new Event("contactsUpdated"));
      } catch {
        // event dispatch failure is non-critical
      }
      onSave();
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to link contact.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Link Contact to {client?.name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* Search Section */}
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Search for an existing contact to link, or create a new one.
            </Typography>

            <Box display="flex" gap={1} alignItems="center">
              <Autocomplete
                fullWidth
                options={availableContacts}
                getOptionLabel={(option: any) =>
                  `${option.name} (${option.email})` || ""
                }
                value={selectedContact}
                onChange={(_, newValue) => setSelectedContact(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search for a contact" size="small" />
                )}
              />
              <Button
                variant="outlined"
                onClick={onCreateNew}
                sx={{
                  minWidth: "130px",
                  height: "40px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                startIcon={<AddIcon />}
              >
                Create New
              </Button>
            </Box>

            {/* Relationship Form (Only shows when contact is selected) */}
            <Collapse in={!!selectedContact}>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: "background.default" }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Define relationship for{" "}
                  <strong>{selectedContact?.name}</strong>:
                </Typography>
                <Stack spacing={2} pt={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Relationship Type</InputLabel>
                    <Select
                      value={relationshipForm.relationshipType}
                      label="Relationship Type"
                      onChange={(e) =>
                        setRelationshipForm({
                          ...relationshipForm,
                          relationshipType: e.target.value,
                        })
                      }
                    >
                      {relationshipOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={relationshipForm.isPrimary}
                        onChange={(e) =>
                          setRelationshipForm({
                            ...relationshipForm,
                            isPrimary: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Set as Primary Contact"
                  />
                </Stack>
              </Paper>
            </Collapse>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleLinkContact}
          variant="contained"
          disabled={
            !selectedContact || !relationshipForm.relationshipType || isLoading
          }
        >
          {isLoading ? <CircularProgress size={24} /> : "Link Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UnlinkConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  contactName,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle>Unlink Contact</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to unlink <strong>{contactName}</strong>? This
        action will not delete the contact itself.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} variant="outlined">
        Cancel
      </Button>
      <Button onClick={onConfirm} variant="contained" color="error">
        Unlink
      </Button>
    </DialogActions>
  </Dialog>
);

const DeleteMeetingDialog = ({ open, onClose, onConfirm, meetingName, isDeleting }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle sx={{ textAlign: "center" }}>
      <WarningIcon color="error" sx={{ fontSize: 40 }} />
    </DialogTitle>
    <DialogContent>
      <Typography variant="h6" align="center">
        Delete Meeting?
      </Typography>
      <Typography align="center" color="text.secondary">
        Are you sure you want to delete the meeting with{" "}
        <strong>{meetingName}</strong>?
      </Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "center", p: 2 }}>
      <Button onClick={onClose} variant="outlined" disabled={isDeleting}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color="error"
        disabled={isDeleting}
        startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : null}
      >
        {isDeleting ? "Deleting…" : "Delete"}
      </Button>
    </DialogActions>
  </Dialog>
);



// type HomeSortField = "createdAt" | "name" | "updatedAt";
// type HomeSortOrder = "asc" | "desc";

// const HOME_CLIENT_SORT_DEFAULT = { field: "createdAt" as HomeSortField, order: "desc" as HomeSortOrder };
// const HOME_CONTACT_SORT_DEFAULT = { field: "createdAt" as HomeSortField, order: "desc" as HomeSortOrder };


const Homepage = () => {
  const { login } = useContext(DataContext);
  const navigate = useNavigate();
  const [setAlert] = useAlert();

  const {
    meetings,
    clients,
    advisors,
    meetingTypes,
    isMeetingsLoading,
    viewMode,
    setViewMode,
    fetchMeetingsData,
    fetchClientsData,
    fetchAllContactsData,
  } = useHomepageData({ login });

  // Persisted so returning to Home reopens the same Clients/Contacts view instead of
  // always resetting to Clients.
  const [activeView] = useSessionState("homepage:activeView", "clients");

  const {
    expandedClientId,
    clientContacts,
    fetchClientContacts,
  } = useClientExpand();

  const {
    isLinkModalOpen, setIsLinkModalOpen,
    contactsRefreshCounter, setContactsRefreshCounter,
    isAddContactModalOpen, setIsAddContactModalOpen,
    isEditContactModalOpen, setIsEditContactModalOpen,
    isAddClientModalOpen, setIsAddClientModalOpen,
    isEditClientModalOpen, setIsEditClientModalOpen,
    isDeleteClientDialogOpen, setIsDeleteClientDialogOpen,
    isDeleteContactDialogOpen, setIsDeleteContactDialogOpen,
    isUnlinkConfirmOpen, setIsUnlinkConfirmOpen,
    isMeetingDeleteDialogOpen, setIsMeetingDeleteDialogOpen,
    isDeletingMeeting, setIsDeletingMeeting,
    isImportContactModalOpen, setIsImportContactModalOpen,
    returnToLinkMode, setReturnToLinkMode,
  } = useHomepageModals();

  const { schedulerState, setSchedulerState, closeScheduler } = useSchedulerState();
  const {
    selectedClientIds, setSelectedClientIds,
    selectedContactIds, setSelectedContactIds,
  } = useCRMSelection();

  const [selectedClientForModal, setSelectedClientForModal] =
    useState<Client | null>(null);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const handleSwitchToCreateContact = () => {
    setIsLinkModalOpen(false);
    setSelectedContact(null);
    setIsAddContactModalOpen(true);
    setReturnToLinkMode(true);
  };

  // Retained only to satisfy MeetingsPanel's prop contract — the status/type
  // filter UI was removed in #2080, so this never changes from its neutral value.
  const [filters, setFilters] = useState({ status: "All", meetingType: "All" });
  // Granularity of the list-view chart, plus the period it's anchored on.
  // Year by default — the widest view, so nothing is hidden on first load.
  const [listGranularity, setListGranularity] = useState<ListGranularity>("year");
  const [listAnchor, setListAnchor] = useState<dayjs.Dayjs>(() => dayjs());
  const [selectedBucketKey, setSelectedBucketKey] = useState<string | null>(null);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));



  // Function to fetch full client data and open edit modal (for AI-triggered edits)
  const fetchClientDataAndOpenEditModal = async (
    clientId: string,
    clientName?: string,
  ) => {
    try {
      const response: any = await fetchClientById(clientId);
      if (response && response._id) {
        setSelectedClientForModal(response);
        setIsEditClientModalOpen(true);
      } else {
        // Fallback: create a minimal client object with required properties
        setSelectedClientForModal({
          _id: clientId,
          name: clientName || "Unknown Client",
          entityType: "Individual",
          orgId: login.data.orgId,
        } as Client);
        setIsEditClientModalOpen(true);
      }
    } catch {
      // Fallback: create a minimal client object with required properties
      setSelectedClientForModal({
        _id: clientId,
        name: clientName || "Unknown Client",
        entityType: "Individual",
        orgId: login.data.orgId,
      } as Client);
      setIsEditClientModalOpen(true);
    }
  };

  // Function to fetch full contact data and open edit modal (for AI-triggered edits)
  const fetchContactDataAndOpenEditModal = async (
    contactId: string,
    contactName?: string,
  ) => {
    try {
      const response: any = await fetchContactById(contactId);
      if (response && response._id) {
        setSelectedContact(response);
        setIsEditContactModalOpen(true);
      } else {
        // Fallback: create a minimal contact object
        setSelectedContact({
          _id: contactId,
          name: contactName || "Unknown Contact",
        });
        setIsEditContactModalOpen(true);
      }
    } catch {
      // Fallback: create a minimal contact object
      setSelectedContact({
        _id: contactId,
        name: contactName || "Unknown Contact",
      });
      setIsEditContactModalOpen(true);
    }
  };

  useAIModalBridge({
    setSchedulerState,
    setSelectedClientForModal,
    setIsEditClientModalOpen,
    setIsAddClientModalOpen,
    setSelectedContact,
    setIsEditContactModalOpen,
    setIsAddContactModalOpen,
    setIsImportContactModalOpen,
    fetchClientDataAndOpenEditModal,
    fetchContactDataAndOpenEditModal,
  });

  useZohoCallback();

  const handleOpenMeetingModal = async (
    type: "edit" | "delete",
    meeting: Meeting,
  ) => {
    if (type === "delete") {
      setSelectedMeeting(meeting);
      setIsMeetingDeleteDialogOpen(true);
      return;
    }

    if (type === "edit") {
      try {
        const response: any = await fetchMeetingById(meeting._id);
        const fullMeetingDetails = (response as any).meeting || response;

        setSchedulerState({
          open: true,
          mode: "edit",
          initialData: fullMeetingDetails,
          context: "global",
        });
      } catch (error) {
        setAlert("Failed to load full meeting details.", "error");
      }
    }
  };

  const handleDeleteMeeting = async () => {
    if (!selectedMeeting) return;
    setIsDeletingMeeting(true);
    try {
      await deleteMeeting(selectedMeeting._id);
      setAlert("Meeting deleted successfully", "success");
      fetchMeetingsData();
    } catch (error) {
      setAlert("Failed to delete meeting", "error");
    } finally {
      setIsDeletingMeeting(false);
      setIsMeetingDeleteDialogOpen(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClientForModal) return;
    try {
      const ctx = { userId: login?.data?.id, orgId: login?.data?.orgId };
      if (selectedClientIds.length > 1) {
        await Promise.all(selectedClientIds.map((clientId) => deleteClient(clientId)));
        selectedClientIds.forEach((id) =>
          deleteClientGraph(id, ctx).catch(() => {}),
        );
        setAlert(
          `${selectedClientIds.length} clients deleted successfully`,
          "success",
        );
        setSelectedClientIds([]);
      } else {
        const clientId =
          selectedClientIds.length === 1
            ? selectedClientIds[0]
            : selectedClientForModal._id;
        await deleteClient(clientId);
        deleteClientGraph(clientId, ctx).catch(() => {});
        setAlert("Client deleted successfully", "success");
        setSelectedClientIds([]);
      }
      fetchClientsData();
    } catch (error) {
      setAlert("Failed to delete client(s)", "error");
    } finally {
      setIsDeleteClientDialogOpen(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    try {
      if (selectedContactIds.length > 1) {
        await Promise.all(selectedContactIds.map((contactId) => deleteContact(contactId)));
        setAlert(
          `${selectedContactIds.length} contacts deleted successfully`,
          "success",
        );
        setSelectedContactIds([]);
      } else {
        const contactId =
          selectedContactIds.length === 1
            ? selectedContactIds[0]
            : selectedContact._id;
        await deleteContact(contactId);
        setAlert("Contact deleted successfully", "success");
        setSelectedContactIds([]);
      }
      fetchAllContactsData();
    } catch (error) {
      setAlert("Failed to delete contact(s)", "error");
    } finally {
      setIsDeleteContactDialogOpen(false);
    }
  };


  const handleUnlinkContact = async () => {
    if (!selectedContact || !expandedClientId) return;
    try {
      const payload = {
        clientId: expandedClientId,
        contactId: selectedContact._id,
        relationshipType: selectedContact.relationshipType,
      };
      await unlinkContact(payload);
      deleteContactGraph({
        ...payload,
        _id: payload.contactId,
      }).catch(() => {});
      setAlert("Contact unlinked successfully", "success");
      fetchClientContacts(expandedClientId);
    } catch (error) {
      setAlert("Failed to unlink contact", "error");
    } finally {
      setIsUnlinkConfirmOpen(false);
    }
  };

  const { rangeStart, rangeEnd, bucketDefs } = useMemo(() => {
    const { rangeStart, rangeEnd, buckets } = buildBuckets(listGranularity, listAnchor);
    return { rangeStart, rangeEnd, bucketDefs: buckets };
  }, [listGranularity, listAnchor]);

  /** Human label for the period the chart is showing, e.g. "Aug 2026". */
  const rangeLabel = useMemo(() => {
    if (listGranularity === "day") return listAnchor.format("ddd D MMM YYYY");
    if (listGranularity === "week")
      return `${rangeStart.format("D MMM")} – ${rangeEnd.format("D MMM YYYY")}`;
    if (listGranularity === "month") return listAnchor.format("MMMM YYYY");
    // "FY 2026–27" — the convention advisors use, and it makes the July start explicit.
    if (listGranularity === "fy")
      return `FY ${rangeStart.format("YYYY")}–${rangeEnd.format("YY")}`;
    return listAnchor.format("YYYY");
  }, [listGranularity, listAnchor, rangeStart, rangeEnd]);

  const kpiStats = useMemo(() => {
    const now = dayjs();
    const scoped = (meetings || []).filter((m: any) =>
      getMeetingStart(m).isBetween(rangeStart, rangeEnd, null, "[]"),
    );
    return {
      totalMeetings: scoped.length,
      upcomingMeetings: scoped.filter((m: any) => getMeetingStart(m).isAfter(now)).length,
      meetingsToday: scoped.filter((m: any) => getMeetingStart(m).isSame(now, "day")).length,
    };
  }, [meetings, rangeStart, rangeEnd]);

  const rangeFilteredMeetings = useMemo(
    () =>
      (meetings || []).filter((m: any) =>
        getMeetingStart(m).isBetween(rangeStart, rangeEnd, null, "[]"),
      ),
    [meetings, rangeStart, rangeEnd],
  );

  const meetingBuckets = useMemo(
    () =>
      bucketDefs.map((b) => ({
        key: b.key,
        label: b.label,
        subLabel: b.subLabel,
        tooltipLabel: b.tooltipLabel,
        count: rangeFilteredMeetings.filter((m: any) =>
          getMeetingStart(m).isBetween(b.start, b.end, null, "[]"),
        ).length,
      })),
    [bucketDefs, rangeFilteredMeetings],
  );

  const filteredMeetingsForTable = useMemo(() => {
    let arr = rangeFilteredMeetings;
    if (selectedBucketKey) {
      const bucket = bucketDefs.find((b) => b.key === selectedBucketKey);
      if (bucket) {
        arr = arr.filter((m: any) =>
          getMeetingStart(m).isBetween(bucket.start, bucket.end, null, "[]"),
        );
      }
    }
    return arr;
  }, [rangeFilteredMeetings, selectedBucketKey, bucketDefs]);

  // A bucket key only means something within one period — changing granularity
  // or navigating would otherwise leave a stale selection filtering the list.
  useEffect(() => {
    setSelectedBucketKey(null);
  }, [listGranularity, listAnchor]);


  const { runTour, setRunTour, tourStepIndex, handleJoyrideCallback } = useHomepageTour(
    login?.data?.hasAcceptedToS !== false,
  );

  return (
    <Container
      // On lg+ the dashboard is a fixed-height app view: the page itself does
      // not scroll, each panel scrolls internally. Below lg the columns stack
      // and the page scrolls normally, so the height chain is not applied.
      parentContentStyle={
        isLargeScreen
          ? {
              height: "calc(100vh - 68px)",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }
          : undefined
      }
      contentStyle={
        isLargeScreen
          ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <Joyride
        continuous
        run={runTour}
        stepIndex={tourStepIndex}
        steps={homepageTourSteps}
        callback={handleJoyrideCallback}
        showProgress
        showSkipButton
        disableOverlayClose={false}
        disableCloseOnEsc={false}
        spotlightClicks={false}
        disableScrolling={true}
        disableScrollParentFix={true}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: {
              filter: "none",
            },
          },
        }}
        styles={{
          options: { primaryColor: "#1976d2", zIndex: 10000 },
          tooltip: { borderRadius: "12px", padding: "16px 20px" },
          buttonNext: { backgroundColor: "#1976d2", borderRadius: "8px" },
          buttonBack: { color: "#555" },
        }}
      />
      <Box
        sx={{
          pb: 0,
          display: { lg: "flex" },
          flexDirection: { lg: "column" },
          flex: { lg: 1 },
          minHeight: { lg: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 1, md: 1 },
            mb: { xs: 0.5, md: 0 },
          }}
        >
          <Box id="welcome-header" sx={{ width: { xs: "100%", md: "auto" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h2" fontWeight="bold">
                Welcome back, {login.data.name?.split(" ")[0]}!
              </Typography>
              <Tooltip title="Start Guided Tour">
                <IconButton
                  id="tour-restart-button-mobile"
                  onClick={() => setRunTour(true)}
                  sx={{ display: { xs: "inline-flex", md: "none" } }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: { xs: 0, sm: 1 } }}
            >
              You have{" "}
              {isMeetingsLoading ? (
                <Skeleton variant="text" animation="wave" sx={{ width: 20, display: "inline-block", verticalAlign: "middle", mx: 0.5 }} />
              ) : (
                kpiStats.meetingsToday
              )}{" "}
              upcoming meetings today.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "center", md: "flex-end" }}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <Button
              id="schedule-meeting-button"
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={() =>
                setSchedulerState({
                  open: true,
                  mode: "create",
                  initialData: null,
                  context: "global",
                })
              }
              sx={{
                width: { xs: "100%", md: "auto" },
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Schedule Meeting
            </Button>
            <Tooltip title="Start Guided Tour">
              <IconButton
                id="tour-restart-button"
                onClick={() => setRunTour(true)}
                sx={{ display: { xs: "none", md: "inline-flex" } }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {(() => {
          const filterBar = (
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ flexShrink: 0 }}
            >
                <Stack
                  id="meeting-filters"
                  direction="row"
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>View</InputLabel>
                    <Select
                    sx={{bgcolor: "white"}}
                      value={viewMode}
                      label="View"
                      onChange={(e) => setViewMode(e.target.value as "my" | "all")}
                    >
                      <MenuItem value="my">My Meetings</MenuItem>
                      <MenuItem value="all">All Meetings</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                    sx={{bgcolor: "white"}}
                      value={filters.status}
                      label="Status"
                      onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value="Upcoming">Upcoming</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFilters({ status: "All", meetingType: "All" });
                      setViewMode("my");
                      setSelectedBucketKey(null);
                    }}
                    sx={{ height: "40px" }}
                  >
                    Clear
                  </Button>
                </Stack>
            </Box>
          );

          const kpiCards = (
            <Box
              id="kpi-grid"
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexShrink: 0,
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <KpiCard
                  title="Total Meetings"
                  value={
                    isMeetingsLoading ? (
                      <Skeleton
                        variant="text"
                        animation="wave"
                        sx={{ width: 60, height: 36 }}
                      />
                    ) : (
                      kpiStats.totalMeetings
                    )
                  }
                  change={null}
                  icon={<EventIcon />}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <KpiCard
                  title="Meetings Today"
                  value={
                    isMeetingsLoading ? (
                      <Skeleton
                        variant="text"
                        animation="wave"
                        sx={{ width: 60, height: 36 }}
                      />
                    ) : (
                      kpiStats.meetingsToday
                    )
                  }
                  change={null}
                  icon={<TaskAltIcon />}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <KpiCard
                  title="Upcoming Meetings"
                  subtitle={rangeLabel}
                  value={
                    isMeetingsLoading ? (
                      <Skeleton
                        variant="text"
                        animation="wave"
                        sx={{ width: 60, height: 36 }}
                      />
                    ) : (
                      kpiStats.upcomingMeetings
                    )
                  }
                  change={null}
                  icon={<TodayIcon />}
                  // The status filter is gone (#2080), so this now just clears any
                  // bar selection and takes you to the full list for the period.
                  onClick={() => {
                    setSelectedBucketKey(null);
                    document
                      .getElementById("meetings-panel")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                />
              </Box>
            </Box>
          );

          return (
            <Grid
              container
              spacing={3}
              sx={{
                mt: { xs: "4px", md: 0 },
                flex: { lg: 1 },
                minHeight: { lg: 0 },
                // A wrapping flex container sizes its line from the items, so
                // `height: 100%` inside a Grid item never resolves. nowrap makes
                // the line take the container's height, which is what lets the
                // meetings panel fill it.
                flexWrap: { lg: "nowrap" },
              }}
            >
              {!isLargeScreen && <Grid item xs={12}>{kpiCards}</Grid>}

              <Grid item xs={12}>
                {/* Flex column so the meetings panel absorbs whatever height is
                    left after the filters and KPIs — otherwise the column is
                    taller than the viewport and the page scrolls. */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: { xs: "auto", lg: "100%" },
                    minHeight: 0,
                  }}
                >
                  {filterBar}
                  {isLargeScreen && kpiCards}

                  <MeetingsPanel
                    meetings={filteredMeetingsForTable}
                    allMeetings={meetings}
                    clients={clients}
                    isMeetingsLoading={isMeetingsLoading}
                    filters={{ status: "All", meetingType: "All" }}
                    setFilters={setFilters}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    selectedBucketKey={selectedBucketKey}
                    setSelectedBucketKey={setSelectedBucketKey}
                    meetingBuckets={meetingBuckets}
                    listGranularity={listGranularity}
                    onListGranularityChange={(g) => {
                      setListGranularity(g);
                      setListAnchor(dayjs());
                    }}
                    granularityOptions={LIST_GRANULARITIES}
                    rangeLabel={rangeLabel}
                    onListPeriodStep={(direction) =>
                      setListAnchor((prev) => {
                        // dayjs has no "fy" unit — stepping a whole year off the
                        // FY's own start keeps the 1 Jul anchor exact.
                        if (listGranularity === "fy") {
                          return financialYearStart(prev).add(direction, "year");
                        }
                        return prev.add(direction, listGranularity);
                      })
                    }
                    onListPeriodToday={() => setListAnchor(dayjs())}
                    onScheduleMeeting={() =>
                      setSchedulerState({
                        open: true,
                        mode: "create",
                        initialData: null,
                        context: "global",
                      })
                    }
                    onEditMeeting={(m) => handleOpenMeetingModal("edit", m)}
                    onDeleteMeeting={(m) => handleOpenMeetingModal("delete", m)}
                    onMeetingAdded={() => fetchMeetingsData()}
                  />
                </Box>
              </Grid>
            </Grid>
          );
        })()}

        {/* ALL MODALS AND DIALOGS */}
        {selectedClientForModal && (
          <LinkContactModal
            open={isLinkModalOpen}
            onClose={() => setIsLinkModalOpen(false)}
            onSave={() => {
              // Refresh client contacts and signal MeetingScheduler (and children)
              fetchClientContacts(expandedClientId);
              setContactsRefreshCounter((c) => c + 1);
            }}
            client={selectedClientForModal}
            orgId={login.data.orgId}
            userId={login.data.id}
            existingLinkedContacts={clientContacts}
            onCreateNew={handleSwitchToCreateContact}
          />
        )}

        <ConfirmDeleteModal
          open={isDeleteClientDialogOpen}
          entityType="client"
          entityName={selectedClientForModal?.name ?? ''}
          onConfirm={handleDeleteClient}
          onCancel={() => setIsDeleteClientDialogOpen(false)}
        />

        <ConfirmDeleteModal
          open={isDeleteContactDialogOpen}
          entityType="contact"
          entityName={selectedContact?.name ?? ''}
          onConfirm={handleDeleteContact}
          onCancel={() => setIsDeleteContactDialogOpen(false)}
        />

        <UnlinkConfirmationDialog
          open={isUnlinkConfirmOpen}
          onClose={() => setIsUnlinkConfirmOpen(false)}
          onConfirm={handleUnlinkContact}
          contactName={selectedContact?.name}
        />

        <ClientModal
          open={isAddClientModalOpen || isEditClientModalOpen}
          // Note: You should refactor your state to just isClientModalOpen in the future,
          // but for now, I will map your existing state to the new component logic
          // so we don't break the rest of the file structure too much.
          onClose={() => {
            setIsAddClientModalOpen(false);
            setIsEditClientModalOpen(false);
            setSelectedClientForModal(null);
          }}
          onSave={fetchClientsData}
          clientData={selectedClientForModal} // If null, it's create mode. If set, it's edit mode.
        />

        <ContactModal
          open={isAddContactModalOpen || isEditContactModalOpen}
          onClose={() => {
            // Close the Create/Edit Contact modal without triggering the Link flow.
            // The Link flow should only be triggered after a successful save (handled in onSave).
            setIsAddContactModalOpen(false);
            setIsEditContactModalOpen(false);
            setSelectedContact(null);
            // Clear the returnToLinkMode flag when the user cancels so we don't reopen the Link modal unexpectedly
            setReturnToLinkMode(false);
          }}
          onSave={() => {
            fetchAllContactsData();
            if (expandedClientId) fetchClientContacts(expandedClientId);

            // Signal MeetingScheduler to refresh its contacts
            setContactsRefreshCounter((c) => c + 1);

            // If saved successfully, go back to linking
            if (returnToLinkMode) {
              setIsAddContactModalOpen(false);
              setIsLinkModalOpen(true);
              setReturnToLinkMode(false);
            }
          }}
          contactData={selectedContact}
        />

        <DeleteMeetingDialog
          open={isMeetingDeleteDialogOpen}
          onClose={() => setIsMeetingDeleteDialogOpen(false)}
          onConfirm={handleDeleteMeeting}
          meetingName={selectedMeeting?.clients?.map((c) => c.name).join(", ")}
          isDeleting={isDeletingMeeting}
        />

        <MeetingScheduler
          open={schedulerState.open}
          mode={schedulerState.mode}
          initialData={schedulerState.initialData}
          context={schedulerState.context}
          onClose={closeScheduler}
          onSave={fetchMeetingsData}
          clients={clients}
          advisors={advisors}
          login={login}
          meetingTypes={meetingTypes}
          onOpenCreateContact={(clientId) => {
            setSelectedContact(null);
            // If a clientId is provided, set the selected client for the Link modal
            if (clientId) {
              const found = clients.find((c) => c._id === clientId) || null;
              setSelectedClientForModal(found);
              setReturnToLinkMode(true);
            } else {
              // If no clientId provided (global create), do not set returnToLinkMode
              setSelectedClientForModal(null);
              setReturnToLinkMode(false);
            }
            setIsAddContactModalOpen(true);
          }}
          onOpenLinkContact={(clientId) => {
            // Open LinkContactModal and pre-select client if provided
            setSelectedContact(null);
            if (clientId) {
              const found = clients.find((c) => c._id === clientId) || null;
              setSelectedClientForModal(found);
            } else {
              setSelectedClientForModal(null);
            }
            setIsLinkModalOpen(true);
          }}
          contactsRefreshCounter={contactsRefreshCounter}
          onUploadSuccess={(meetingId, meetingData) => {
            navigate(`/meetings/${meetingId}`, {
              state: { meeting: meetingData, triggerTranscription: true },
            });
          }}
        />

        <ImportContactDialog
          open={isImportContactModalOpen}
          onClose={() => setIsImportContactModalOpen(false)}
          onSave={fetchAllContactsData}
          activeView={activeView}
          setIsAddClientModalOpen={setIsAddClientModalOpen}
        />
      </Box>
    </Container>
  );
};

export default Homepage;
